import React, { useEffect, useRef, useState } from "react";
import { Camera, Upload, RefreshCw, Sparkles, Download, Share2, PlusCircle, X } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { TEST_IDS } from "@/constants/testIds";
import { composeStickerWithPhrase, downloadDataUrl, shareSticker } from "@/lib/sticker";
import { useUnlock } from "@/lib/payments";
import PaywallModal from "@/components/PaywallModal";
import SocialProof from "@/components/SocialProof";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  { key: "", label: "SORPRÉNDEME" },
  { key: "hype", label: "PORRA" },
  { key: "goal", label: "GOOOL" },
  { key: "celebration", label: "FIESTA" },
  { key: "trash_talk", label: "PICOTEO" },
];

const STYLES = [
  { key: "basic", label: "CLÁSICO TRI", premium: false, emoji: "⚽" },
  { key: "vintage_86", label: "VINTAGE '86", premium: true, emoji: "🌶️" },
  { key: "lucha_libre", label: "LUCHA LIBRE", premium: true, emoji: "🎭" },
  { key: "holografico", label: "HOLOGRÁFICO", premium: true, emoji: "✨" },
];

const TEAMS = [
  { key: "mexico", label: "🇲🇽 México" },
  { key: "argentina", label: "🇦🇷 Argentina" },
  { key: "brasil", label: "🇧🇷 Brasil" },
  { key: "espana", label: "🇪🇸 España" },
  { key: "francia", label: "🇫🇷 Francia" },
  { key: "alemania", label: "🇩🇪 Alemania" },
  { key: "portugal", label: "🇵🇹 Portugal" },
  { key: "usa", label: "🇺🇸 USA" },
  { key: "colombia", label: "🇨🇴 Colombia" },
  { key: "uruguay", label: "🇺🇾 Uruguay" },
];

const STEPS = { INTRO: "intro", CAPTURE: "capture", PREVIEW: "preview", GENERATING: "generating", RESULT: "result" };

export default function Home() {
  const [step, setStep] = useState(STEPS.INTRO);
  const [category, setCategory] = useState("");
  const [style, setStyle] = useState("basic");
  const [team, setTeam] = useState("mexico");
  const [selfieDataUrl, setSelfieDataUrl] = useState(null);
  const [finalSticker, setFinalSticker] = useState(null); // composed dataURL
  const [phrase, setPhrase] = useState("");
  const [error, setError] = useState("");
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [paywallReason, setPaywallReason] = useState("Descargar");
  const [paywallPlan, setPaywallPlan] = useState("basic");
  const { unlocked, premiumUnlocked } = useUnlock();

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    return () => stopCamera();
  }, []);

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const startCamera = async () => {
    setError("");
    setStep(STEPS.CAPTURE);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 1280 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch (e) {
      setError("No pudimos acceder a la cámara. Prueba subir una foto.");
      setStep(STEPS.INTRO);
    }
  };

  const captureFromVideo = () => {
    if (!videoRef.current) return;
    const v = videoRef.current;
    const s = Math.min(v.videoWidth, v.videoHeight);
    const canvas = document.createElement("canvas");
    canvas.width = s;
    canvas.height = s;
    const ctx = canvas.getContext("2d");
    const sx = (v.videoWidth - s) / 2;
    const sy = (v.videoHeight - s) / 2;
    ctx.drawImage(v, sx, sy, s, s, 0, 0, s, s);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
    stopCamera();
    setSelfieDataUrl(dataUrl);
    setStep(STEPS.PREVIEW);
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      setSelfieDataUrl(reader.result);
      setStep(STEPS.PREVIEW);
    };
    reader.readAsDataURL(f);
  };

  const retake = () => {
    setSelfieDataUrl(null);
    setFinalSticker(null);
    setPhrase("");
    setStep(STEPS.INTRO);
  };

  const generate = async () => {
    if (!selfieDataUrl) return;

    // Gate premium styles client-side too
    const chosen = STYLES.find((s) => s.key === style);
    if (chosen?.premium && !premiumUnlocked) {
      setPaywallReason("Usar estilo premium");
      setPaywallPlan("premium");
      setPaywallOpen(true);
      return;
    }

    setError("");
    setStep(STEPS.GENERATING);
    try {
      const b64 = selfieDataUrl.split(",")[1];
      const deviceId = localStorage.getItem("caliche_device_id") || "";
      const resp = await axios.post(`${API}/generate-sticker`, {
        selfie_base64: b64,
        category: category || undefined,
        style,
        team,
        device_id: deviceId,
      }, { timeout: 120000 });

      const { image_base64, phrase: pFrase } = resp.data;
      const composed = await composeStickerWithPhrase(image_base64, pFrase);
      setFinalSticker(composed);
      setPhrase(pFrase);
      setStep(STEPS.RESULT);
      toast.success("¡Tu sticker está listo, cabrón!");
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Error generando el sticker";
      setError(String(msg));
      setStep(STEPS.PREVIEW);
      toast.error("No se pudo generar el sticker");
    }
  };

  const onDownload = () => {
    if (!finalSticker) return;
    if (!unlocked) {
      setPaywallReason("Descargar");
      setPaywallPlan("basic");
      setPaywallOpen(true);
      return;
    }
    downloadDataUrl(finalSticker, `caliche-mundial-${Date.now()}.png`);
    toast.success("Descargando...");
  };

  const onShare = async () => {
    if (!finalSticker) return;
    if (!unlocked) {
      setPaywallReason("Compartir");
      setPaywallPlan("basic");
      setPaywallOpen(true);
      return;
    }
    const res = await shareSticker(finalSticker, phrase);
    if (!res.ok) {
      toast.message("Compartir no disponible. Descarga y sube a tus redes 📲");
    }
  };

  const onClickPremiumStyle = (styleKey) => {
    const target = STYLES.find((s) => s.key === styleKey);
    if (target?.premium && !premiumUnlocked) {
      setPaywallReason("Usar estilo premium");
      setPaywallPlan("premium");
      setPaywallOpen(true);
      return;
    }
    setStyle(styleKey);
  };

  return (
    <div className="pt-4">
      {error && (
        <div
          data-testid={TEST_IDS.errorAlert}
          className="mb-4 nb-border bg-[#CE1126] text-white p-3 flex items-start justify-between gap-3"
        >
          <span className="font-outfit text-sm">{error}</span>
          <button onClick={() => setError("")} aria-label="cerrar">
            <X className="w-5 h-5" strokeWidth={3} />
          </button>
        </div>
      )}

      {step === STEPS.INTRO && <Intro onStartCamera={startCamera} onUpload={() => fileInputRef.current?.click()} />}

      {step === STEPS.CAPTURE && (
        <CaptureView videoRef={videoRef} onCapture={captureFromVideo} onCancel={() => { stopCamera(); setStep(STEPS.INTRO); }} />
      )}

      {step === STEPS.PREVIEW && selfieDataUrl && (
        <PreviewView
          selfie={selfieDataUrl}
          category={category}
          setCategory={setCategory}
          style={style}
          setStyle={onClickPremiumStyle}
          premiumUnlocked={premiumUnlocked}
          onGenerate={generate}
          onRetake={retake}
        />
      )}

      {step === STEPS.GENERATING && <Generating />}

      {step === STEPS.RESULT && finalSticker && (
        <ResultView sticker={finalSticker} phrase={phrase} onDownload={onDownload} onShare={onShare} onNew={retake} unlocked={unlocked} />
      )}

      <PaywallModal open={paywallOpen} onClose={() => setPaywallOpen(false)} reason={paywallReason} plan={paywallPlan} />

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="user"
        className="hidden"
        onChange={onFile}
        data-testid={TEST_IDS.fileInput}
      />
    </div>
  );
}

function Intro({ onStartCamera, onUpload }) {
  return (
    <section className="text-center pt-6 sm:pt-10">
      <div className="inline-block bg-[#FFD100] border-[3px] border-black shadow-[4px_4px_0_0_#000] px-3 py-1 rotate-[-2deg] mb-6">
        <p className="font-anton uppercase tracking-widest text-sm">MUNDIAL 2026 · MÉXICO</p>
      </div>

      <h1 className="font-anton uppercase leading-[0.9] text-5xl sm:text-6xl md:text-7xl tracking-tight">
        Sácate tu <span className="text-[#006847]">selfie</span>,
        <br />
        te damos tu <span className="text-[#CE1126]">sticker</span>
        <br />
        con <span className="bg-[#FFD100] px-2 border-[3px] border-black shadow-[3px_3px_0_0_#000] inline-block rotate-[-1deg]">caliche</span>.
      </h1>

      <p className="mt-6 font-outfit text-base sm:text-lg text-black/80 max-w-xl mx-auto">
        Convertimos tu jeta en un sticker de caricatura estilo Tri, con una frase{" "}
        <span className="font-bold">bien mexicana</span> y temática mundialista. Descarga y aviéntalo al chat, cabrón.
      </p>

      <div className="mt-8 flex flex-col sm:flex-row gap-4 items-center justify-center">
        <button
          data-testid={TEST_IDS.startCameraBtn}
          onClick={onStartCamera}
          className="nb-btn font-anton uppercase text-2xl px-6 py-4 bg-[#006847] text-white border-[3px] border-black shadow-[4px_4px_0_0_#000] inline-flex items-center gap-3"
        >
          <Camera strokeWidth={3} className="w-7 h-7" />
          Abrir cámara
        </button>
        <button
          data-testid={TEST_IDS.uploadPhotoBtn}
          onClick={onUpload}
          className="nb-btn font-anton uppercase text-2xl px-6 py-4 bg-white text-black border-[3px] border-black shadow-[4px_4px_0_0_#000] inline-flex items-center gap-3"
        >
          <Upload strokeWidth={3} className="w-7 h-7" />
          Subir foto
        </button>
      </div>

      <div className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl mx-auto text-left">
        <StepCard n="1" title="Selfie" body="Tómate una selfie o sube una foto donde se te vea la carota." color="#006847" />
        <StepCard n="2" title="IA Magia" body="Nuestra IA te convierte en un sticker cartoon con jersey del Tri." color="#CE1126" />
        <StepCard n="3" title="Compárte" body="Descárgalo y avíentalo al chat con una frase de caliche." color="#FFD100" dark />
      </div>

      <SocialProof />
    </section>
  );
}

function StepCard({ n, title, body, color, dark }) {
  return (
    <div className="bg-white border-[3px] border-black shadow-[5px_5px_0_0_#000] p-5">
      <div
        className="w-12 h-12 border-[3px] border-black flex items-center justify-center font-anton text-2xl"
        style={{ background: color, color: dark ? "#000" : "#fff" }}
      >
        {n}
      </div>
      <h3 className="font-anton uppercase text-2xl mt-3">{title}</h3>
      <p className="font-outfit text-sm text-black/70 mt-1">{body}</p>
    </div>
  );
}

function CaptureView({ videoRef, onCapture, onCancel }) {
  return (
    <section className="pt-4">
      <h2 className="font-anton uppercase text-3xl sm:text-4xl">Ponte al centro, mi rey</h2>
      <p className="font-outfit text-sm text-black/70 mb-4">Sonríe, haz cara de mundialista, lo que sea.</p>

      <div className="relative mx-auto max-w-md aspect-square border-[4px] border-black shadow-[6px_6px_0_0_#000] bg-black overflow-hidden">
        <video
          ref={videoRef}
          data-testid={TEST_IDS.cameraVideo}
          playsInline
          muted
          className="w-full h-full object-cover"
        />
        {/* Framing corners */}
        <CornerDeco />
      </div>

      <div className="mt-6 flex items-center justify-center gap-3">
        <button
          onClick={onCancel}
          className="nb-btn font-anton uppercase px-5 py-3 bg-white border-[3px] border-black shadow-[4px_4px_0_0_#000]"
        >
          Cancelar
        </button>
        <button
          data-testid={TEST_IDS.captureBtn}
          onClick={onCapture}
          className="nb-btn font-anton uppercase text-2xl px-8 py-4 bg-[#CE1126] text-white border-[3px] border-black shadow-[5px_5px_0_0_#000] inline-flex items-center gap-3"
        >
          <Camera strokeWidth={3} /> ¡Foto!
        </button>
      </div>
    </section>
  );
}

function CornerDeco() {
  const base = "absolute w-8 h-8 border-white";
  return (
    <>
      <div className={`${base} top-3 left-3 border-t-4 border-l-4`} />
      <div className={`${base} top-3 right-3 border-t-4 border-r-4`} />
      <div className={`${base} bottom-3 left-3 border-b-4 border-l-4`} />
      <div className={`${base} bottom-3 right-3 border-b-4 border-r-4`} />
    </>
  );
}

function PreviewView({ selfie, category, setCategory, style, setStyle, team, setTeam, premiumUnlocked, onGenerate, onRetake }) {
  return (
    <section className="pt-4">
      <h2 className="font-anton uppercase text-3xl sm:text-4xl">Ya quedó, ¿le damos?</h2>
      <p className="font-outfit text-sm text-black/70 mb-4">Elige tu equipo, estilo y tono de la frase.</p>

      <div className="grid md:grid-cols-2 gap-6 items-start">
        <div className="mx-auto md:mx-0 w-full max-w-md aspect-square border-[4px] border-black shadow-[6px_6px_0_0_#000] bg-white overflow-hidden">
          <img src={selfie} alt="preview" className="w-full h-full object-cover" />
        </div>

        <div>
          <label className="font-anton uppercase text-lg block mb-2">
            Tu equipo
          </label>
          <div className="flex flex-wrap gap-2">
            {TEAMS.map((t) => {
              const active = team === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setTeam(t.key)}
                  data-testid={`team-${t.key}`}
                  className={`font-anton uppercase text-xs px-3 py-2 border-[3px] border-black transition-all duration-150 ${
                    active
                      ? "bg-black text-white shadow-none translate-x-[3px] translate-y-[3px]"
                      : "bg-white shadow-[3px_3px_0_0_#000] hover:-translate-y-0.5 hover:shadow-[5px_5px_0_0_#000]"
                  }`}
                >
                  {t.label}
                </button>
              );
            })}
          </div>

          <label className="font-anton uppercase text-lg block mb-2 mt-5">
            Estilo del sticker
          </label>
          <div className="flex flex-wrap gap-2">
            {STYLES.map((s) => {
              const active = style === s.key;
              const locked = s.premium && !premiumUnlocked;
              return (
                <button
                  key={s.key}
                  onClick={() => setStyle(s.key)}
                  data-testid={`style-${s.key}`}
                  className={`relative font-anton uppercase text-xs px-3 py-2 border-[3px] border-black transition-all duration-150 ${
                    active
                      ? "bg-black text-white shadow-none translate-x-[3px] translate-y-[3px]"
                      : "bg-white shadow-[3px_3px_0_0_#000] hover:-translate-y-0.5 hover:shadow-[5px_5px_0_0_#000]"
                  }`}
                >
                  <span className="mr-1">{s.emoji}</span>
                  {s.label}
                  {locked && (
                    <span className="ml-1.5 inline-flex items-center justify-center bg-[#CE1126] text-white px-1 border-[2px] border-black text-[9px]">
                      $50
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          {!premiumUnlocked && (
            <p className="mt-2 font-outfit text-xs text-black/60">
              Los estilos con <span className="font-bold">$50</span> son parte del Pack Chingón Premium.
            </p>
          )}

          <label className="font-anton uppercase text-lg block mb-2 mt-5" data-testid={TEST_IDS.categorySelect}>
            Tono de la frase
          </label>
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((c) => {
              const active = category === c.key;
              return (
                <button
                  key={c.key || "any"}
                  onClick={() => setCategory(c.key)}
                  data-testid={`category-${c.key || "any"}`}
                  className={`font-anton uppercase text-sm px-3 py-2 border-[3px] border-black transition-all duration-150 ${
                    active
                      ? "bg-[#006847] text-white shadow-none translate-x-[3px] translate-y-[3px]"
                      : "bg-white shadow-[3px_3px_0_0_#000] hover:-translate-y-0.5 hover:shadow-[5px_5px_0_0_#000]"
                  }`}
                >
                  {c.label}
                </button>
              );
            })}
          </div>

          <div className="mt-6 flex flex-col gap-3">
            <button
              data-testid={TEST_IDS.generateBtn}
              onClick={onGenerate}
              className="nb-btn font-anton uppercase text-2xl px-6 py-4 bg-[#CE1126] text-white border-[3px] border-black shadow-[5px_5px_0_0_#000] inline-flex items-center justify-center gap-3"
            >
              <Sparkles strokeWidth={3} /> ¡Generar sticker!
            </button>
            <button
              data-testid={TEST_IDS.retakeBtn}
              onClick={onRetake}
              className="nb-btn font-anton uppercase text-lg px-4 py-3 bg-white border-[3px] border-black shadow-[4px_4px_0_0_#000] inline-flex items-center justify-center gap-2"
            >
              <RefreshCw strokeWidth={3} className="w-5 h-5" /> Tomar otra
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function Generating() {
  return (
    <section data-testid={TEST_IDS.generatingIndicator} className="pt-16 text-center">
      <div className="mx-auto w-24 h-24 border-[4px] border-black bg-[#FFD100] shadow-[6px_6px_0_0_#000] flex items-center justify-center animate-wobble">
        <Sparkles strokeWidth={3} className="w-12 h-12" />
      </div>
      <h2 className="font-anton uppercase text-5xl sm:text-6xl mt-8 animate-flash">GENERANDO…</h2>
      <p className="font-outfit mt-2 text-black/70">Nuestra IA está pintándote el jersey del Tri. Aguántame tantito.</p>
    </section>
  );
}

function ResultView({ sticker, phrase, onDownload, onShare, onNew, unlocked }) {
  return (
    <section className="pt-4 text-center">
      <h2 className="font-anton uppercase text-4xl sm:text-5xl">
        ¡Quedó <span className="text-[#CE1126]">chingón</span>!
      </h2>
      <p
        data-testid={TEST_IDS.stickerPhrase}
        className="font-anton uppercase text-lg mt-1 text-black/80"
      >
        “{phrase}”
      </p>

      {!unlocked && (
        <div className="mt-3 inline-block bg-[#FFD100] border-[3px] border-black shadow-[3px_3px_0_0_#000] px-3 py-1 rotate-[-1deg]">
          <span className="font-anton uppercase text-xs" data-testid="lock-badge">
            🔒 Descarga/compartir con pase de $25 MXN
          </span>
        </div>
      )}

      <div className="mt-6 mx-auto max-w-md relative">
        <div className="border-[4px] border-black shadow-[8px_8px_0_0_#000] bg-white p-2 relative">
          <img
            data-testid={TEST_IDS.stickerPreview}
            src={sticker}
            alt="sticker"
            className={`w-full h-auto block ${!unlocked ? "blur-[3px]" : ""}`}
          />
          {!unlocked && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="bg-black/70 border-[3px] border-white px-4 py-2 rotate-[-6deg]">
                <span className="font-anton uppercase text-white text-2xl">Vista previa</span>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 flex flex-col sm:flex-row gap-3 items-center justify-center">
        <button
          data-testid={TEST_IDS.downloadBtn}
          onClick={onDownload}
          className="nb-btn font-anton uppercase text-xl px-5 py-3 bg-[#006847] text-white border-[3px] border-black shadow-[4px_4px_0_0_#000] inline-flex items-center gap-2"
        >
          <Download strokeWidth={3} /> Descargar
        </button>
        <button
          data-testid={TEST_IDS.shareBtn}
          onClick={onShare}
          className="nb-btn font-anton uppercase text-xl px-5 py-3 bg-[#FFD100] text-black border-[3px] border-black shadow-[4px_4px_0_0_#000] inline-flex items-center gap-2"
        >
          <Share2 strokeWidth={3} /> Compartir
        </button>
        <button
          data-testid={TEST_IDS.newStickerBtn}
          onClick={onNew}
          className="nb-btn font-anton uppercase text-xl px-5 py-3 bg-white border-[3px] border-black shadow-[4px_4px_0_0_#000] inline-flex items-center gap-2"
        >
          <PlusCircle strokeWidth={3} /> Otro sticker
        </button>
      </div>
    </section>
  );
}
